import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { 
  ArrowLeft, BookOpen, FileText, Brain, Target, Upload,
  CheckCircle2, Timer, Database, Files, History, Book, Tag
} from "lucide-react";
import NavBar from "@/components/NavBar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Icon mapping - ALL icons from lucide-react
const iconMap = {
  "upload": Upload,
  "target": Target,
  "book-open": BookOpen,
  "timer": Timer,
  "book": Book,
  "database": Database,
  "files": Files,
  "history": History,
  "file-text": FileText,
  "brain": Brain
};

// Default visual config for features when Firebase data is missing
const FEATURE_DEFAULTS = {
  descriptive_test: { bg_color: "bg-purple-50", border_color: "border-purple-200", icon_bg: "bg-purple-600", cta: "Start Test →" },
  objective_test:   { bg_color: "bg-green-50",  border_color: "border-green-200",  icon_bg: "bg-green-600",  cta: "Practice MCQs →" },
  study_notes:      { bg_color: "bg-blue-50",   border_color: "border-blue-200",   icon_bg: "bg-blue-600",   cta: "View Notes →", highlight: true },
  mock_test:        { bg_color: "bg-orange-50", border_color: "border-orange-200", icon_bg: "bg-orange-600", cta: "Take Test →" },
  ncert_exemplar:   { bg_color: "bg-yellow-50", border_color: "border-yellow-200", icon_bg: "bg-yellow-600", cta: "View →" },
  question_bank:    { bg_color: "bg-indigo-50", border_color: "border-indigo-200", icon_bg: "bg-indigo-600", cta: "Browse →" },
  merge_pdfs:       { bg_color: "bg-pink-50",   border_color: "border-pink-200",   icon_bg: "bg-pink-600",   cta: "Merge →" },
  view_history:     { bg_color: "bg-gray-50",   border_color: "border-gray-200",   icon_bg: "bg-gray-600",   cta: "View →" },
};
const DEFAULT_VISUAL = { bg_color: "bg-blue-50", border_color: "border-blue-200", icon_bg: "bg-blue-600", cta: "Open →" };

export default function SubjectPage({ onSubjectSelect }) {
  const { examType, subject } = useParams();
  const navigate = useNavigate();
  const [pageData, setPageData] = useState(null);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    fetchPageData();
    fetchFeatures();
    // Check if user is logged in
    const userData = localStorage.getItem("user");
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, [examType, subject]);

  const fetchPageData = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/subject-page/${examType}/${subject}`
      );
      const data = await response.json();
      setPageData(data);
    } catch (error) {
      console.error("Failed to fetch subject page:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeatures = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/platform-features`);
      const data = await response.json();
      setFeatures(data.features || []);
    } catch (error) {
      console.error("Failed to fetch features:", error);
    }
  };

  // Generate SEO tags
  const generateSEOTags = () => {
    if (!pageData) return [];
    
    const examUpper = examType.toUpperCase();
    const subjectTitle = pageData.subject_label;
    
    return [
      `${examUpper} ${subjectTitle} Syllabus`,
      `${examUpper} ${subjectTitle} Exam Pattern`,
      `KVS ${examUpper} ${subjectTitle}`,
      `EMRS ${examUpper} ${subjectTitle}`,
      `NVS ${examUpper} ${subjectTitle}`,
      `${examUpper} ${subjectTitle} Notes`,
      `${examUpper} ${subjectTitle} Mock Test`,
      `${examUpper} ${subjectTitle} Practice Questions`,
      `CBSE ${subjectTitle} ${examUpper}`,
      `${examUpper} ${subjectTitle} Preparation`,
      `${examUpper} Teacher Recruitment ${subjectTitle}`,
      `${examUpper} ${subjectTitle} Tier 2`,
      `KVS ${examUpper} ${subjectTitle} Exam Pattern`,
      `EMRS ${examUpper} ${subjectTitle} Exam Pattern`,
      `${examUpper} ${subjectTitle} Study Material`,
      `${examUpper} ${subjectTitle} Previous Papers`,
      `NCERT ${subjectTitle} Notes ${examUpper}`,
      `${examUpper} ${subjectTitle} MCQ Practice`
    ];
  };

  const handleFeatureClick = (route) => {
    // Set subject selection in the SAME format as SubjectSelection.js
    // MCQPractice/Notes/MockTest all read `category` not `exam_type`
    
    // Preserve existing language from subjectSelection if same category+subject
    const existingSelection = JSON.parse(localStorage.getItem('user_subject_selection') || '{}');
    const existingLang = (existingSelection.category === examType && existingSelection.subject === subject)
      ? existingSelection.language
      : undefined;
    
    const selection = {
      category: examType,           // Primary field read by MCQPractice
      categoryLabel: examType.toUpperCase(),
      subject: subject,
      subjectLabel: pageData?.subject_label || subject,
      language: existingLang,        // Preserve language if same subject, else undefined (Notes.js will ask)
      selectedAt: new Date().toISOString(),
      // Legacy compat fields
      exam_type: examType,
      exam: examType,
      subject_label: pageData?.subject_label || subject
    };
    localStorage.setItem("user_subject_selection", JSON.stringify(selection));
    localStorage.setItem("subjectSelection", JSON.stringify(selection));
    
    // Update App.js React state so MCQPractice/Notes get the correct subject immediately
    if (onSubjectSelect) {
      onSubjectSelect(selection);
    }

    // If not logged in, redirect to login with message
    if (!user) {
      toast.info("Please sign in to access this feature");
      localStorage.setItem("returnTo", `/${examType}/${subject}`);
      navigate("/");
      return;
    }

    // Navigate to feature
    navigate(route);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!pageData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-2xl text-gray-600">Subject not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* SEO Meta Tags */}
      {pageData && (
        <Helmet>
          <title>{`EMRS KVS NVS ${examType.toUpperCase()} ${pageData.subject_label} 2026 | Syllabus, Mock Test, MCQ Practice, Notes`}</title>
          <meta 
            name="description" 
            content={`Complete ${examType.toUpperCase()} ${pageData.subject_label} preparation for EMRS, KVS, NVS Tier-2 exam 2026. Syllabus, exam pattern, NCERT study notes, mock tests, MCQ question bank, descriptive test practice. Free study material for teaching recruitment exams.`}
          />
          <meta 
            name="keywords" 
            content={`EMRS ${examType.toUpperCase()} ${pageData.subject_label}, KVS ${examType.toUpperCase()} ${pageData.subject_label}, NVS ${examType.toUpperCase()} ${pageData.subject_label}, ${examType.toUpperCase()} ${pageData.subject_label} syllabus 2026, ${examType.toUpperCase()} ${pageData.subject_label} exam pattern, ${examType.toUpperCase()} ${pageData.subject_label} mock test, ${examType.toUpperCase()} ${pageData.subject_label} MCQ, ${examType.toUpperCase()} ${pageData.subject_label} notes, EMRS ${examType.toUpperCase()} ${pageData.subject_label} tier 2, KVS ${examType.toUpperCase()} ${pageData.subject_label} preparation, ${pageData.subject_label} descriptive test, NCERT ${pageData.subject_label} notes, CBSE ${pageData.subject_label} ${examType.toUpperCase()}, teacher recruitment ${pageData.subject_label}`}
          />
          <meta property="og:title" content={`${examType.toUpperCase()} ${pageData.subject_label} - EMRS KVS NVS Exam Preparation 2026`} />
          <meta property="og:description" content={`Complete syllabus, mock tests, MCQs, notes, and descriptive practice for ${examType.toUpperCase()} ${pageData.subject_label} - EMRS, KVS, NVS Tier-2`} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={`https://descriptivetest.com/${examType}/${subject}`} />
          <link rel="canonical" href={`https://descriptivetest.com/${examType}/${subject}`} />
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://descriptivetest.com/" },
              { "@type": "ListItem", "position": 2, "name": `${examType.toUpperCase()} Subjects`, "item": `https://descriptivetest.com/category/${examType}` },
              { "@type": "ListItem", "position": 3, "name": pageData.subject_label, "item": `https://descriptivetest.com/${examType}/${subject}` }
            ]
          })}</script>
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Course",
            "name": `${examType.toUpperCase()} ${pageData.subject_label} - Complete Exam Preparation`,
            "description": `Complete ${examType.toUpperCase()} ${pageData.subject_label} preparation with syllabus, mock tests, MCQs, notes for EMRS KVS NVS Tier-2 exam`,
            "url": `https://descriptivetest.com/${examType}/${subject}`,
            "provider": { "@type": "Organization", "name": "Descriptive Test", "url": "https://descriptivetest.com" },
            "educationalLevel": `${examType.toUpperCase()} Level`,
            "inLanguage": ["en", "hi"]
          })}</script>
        </Helmet>
      )}

      {/* NavBar with profile */}
      <NavBar />

      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <button
            onClick={() => navigate(`/category/${examType}`)}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to {examType.toUpperCase()} Subjects</span>
          </button>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900">
            {pageData?.subject_label} - {examType.toUpperCase()}
          </h1>
          <p className="text-lg text-gray-600 mt-2">
            KVS, EMRS, NVS Teacher Recruitment Exam | Complete Preparation Guide
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12 space-y-12">
        {/* Features Section - NOW FIRST */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
            🚀 Start Your Preparation
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature) => {
              const IconComponent = iconMap[feature.icon] || BookOpen;
              const isHighlighted = feature.highlight;
              const defaults = FEATURE_DEFAULTS[feature.id] || DEFAULT_VISUAL;
              const bgColor = feature.bg_color || defaults.bg_color;
              const borderColor = feature.border_color || defaults.border_color;
              const iconBg = feature.icon_bg || defaults.icon_bg;
              const ctaText = feature.cta || defaults.cta || "Open →";
              
              return (
                <div
                  key={feature.id}
                  data-testid={`feature-tile-${feature.id}`}
                  onClick={() => handleFeatureClick(feature.route)}
                  className={`relative p-6 rounded-xl cursor-pointer transition-all hover:shadow-xl border-2 ${bgColor} ${borderColor} ${isHighlighted ? 'ring-2 ring-orange-400' : ''}`}
                >
                  {/* Badge (Premium, Coming Soon, etc.) */}
                  {feature.badge && (
                    <div className={`absolute top-4 right-4 ${feature.badge.color} text-white text-xs font-bold px-2 py-1 rounded`}>
                      {feature.badge.text}
                    </div>
                  )}
                  
                  {/* Icon */}
                  <div className={`inline-flex p-3 ${iconBg} rounded-lg mb-4`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  
                  {/* Title */}
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  
                  {/* Description */}
                  <p className="text-sm text-gray-600 mb-4">
                    {feature.description}
                  </p>
                  
                  {/* Features List */}
                  <div className="space-y-2 mb-4">
                    {feature.features_list?.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-xs text-gray-600">{item}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* CTA */}
                  <div className="pt-2 border-t border-gray-200">
                    <span className="text-sm font-semibold text-blue-600">{ctaText}</span>
                  </div>
                  
                  {/* Status */}
                  {feature.status && (
                    <div className="flex justify-between items-center pt-2">
                      <span className="text-sm text-gray-600">{feature.status.label}:</span>
                      <span className={`text-sm font-bold ${feature.status.color}`}>
                        {feature.status.value}
                      </span>
                    </div>
                  )}
                  
                  {/* Warning */}
                  {feature.warning && (
                    <div className="mt-2 text-xs text-orange-600 flex items-center gap-1">
                      <span>⚠️</span>
                      <span>{feature.warning}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Exam Patterns Section - NOW SECOND */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            📋 Exam Patterns - Tier 2
          </h2>
          <p className="text-gray-600 mb-6">
            Complete exam pattern for {examType.toUpperCase()} {pageData?.subject_label} - KVS & EMRS
          </p>
          
          {/* KVS Pattern */}
          <div className="mb-8 p-6 bg-blue-50 rounded-xl border-2 border-blue-200">
            <h3 className="text-xl font-bold text-blue-900 mb-2">
              KVS {examType.toUpperCase()} {pageData?.subject_label?.toUpperCase()} EXAM PATTERN
            </h3>
            <p className="text-sm text-blue-700 mb-4">Kendriya Vidyalaya Sangathan - Tier 2 Written Exam</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
              <div>
                <div className="text-3xl font-bold text-blue-600">
                  {pageData.exam_patterns.kvs.objective}
                </div>
                <div className="text-sm text-gray-600">Objective</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-blue-600">
                  {pageData.exam_patterns.kvs.subjective}
                </div>
                <div className="text-sm text-gray-600">Subjective</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-blue-600">
                  {pageData.exam_patterns.kvs.total_questions}
                </div>
                <div className="text-sm text-gray-600">Questions</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-blue-600">
                  {pageData.exam_patterns.kvs.marks}
                </div>
                <div className="text-sm text-gray-600">Marks</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-blue-600">
                  {pageData.exam_patterns.kvs.duration}
                </div>
                <div className="text-sm text-gray-600">Duration</div>
              </div>
            </div>
          </div>

          {/* EMRS Pattern */}
          <div className="p-6 bg-purple-50 rounded-xl border-2 border-purple-200">
            <h3 className="text-xl font-bold text-purple-900 mb-2">
              EMRS {examType.toUpperCase()} {pageData?.subject_label?.toUpperCase()} EXAM PATTERN
            </h3>
            <p className="text-sm text-purple-700 mb-4">Eklavya Model Residential Schools - Tier 2 Written Exam</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
              <div>
                <div className="text-3xl font-bold text-purple-600">
                  {pageData.exam_patterns.emrs.objective}
                </div>
                <div className="text-sm text-gray-600">Objective</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-purple-600">
                  {pageData.exam_patterns.emrs.subjective}
                </div>
                <div className="text-sm text-gray-600">Subjective</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-purple-600">
                  {pageData.exam_patterns.emrs.total_questions}
                </div>
                <div className="text-sm text-gray-600">Questions</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-purple-600">
                  {pageData.exam_patterns.emrs.marks}
                </div>
                <div className="text-sm text-gray-600">Marks</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-purple-600">
                  {pageData.exam_patterns.emrs.duration}
                </div>
                <div className="text-sm text-gray-600">Duration</div>
              </div>
            </div>
          </div>
        </div>

        {/* Syllabus Topics Section - NOW THIRD */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            📚 Complete {examType.toUpperCase()} {pageData?.subject_label} Syllabus
          </h2>
          <p className="text-gray-600 mb-6">
            Complete CBSE-based syllabus for KVS, EMRS, NVS teacher recruitment exam
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {pageData.topics.map((topic, index) => (
              <div
                key={index}
                className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 text-sm">{topic}</span>
              </div>
            ))}
          </div>
        </div>

        {/* SEO Tags Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="flex items-center gap-2 mb-4">
            <Tag className="w-6 h-6 text-blue-600" />
            <h3 className="text-xl font-bold text-gray-900">Related Keywords & Topics</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {generateSEOTags().map((tag, idx) => (
              <span
                key={idx}
                className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm border border-blue-200 hover:bg-blue-100 transition-colors"
              >
                {tag}
              </span>
            ))}
          </div>
          <p className="text-sm text-gray-500 mt-4">
            Popular searches: {examType.toUpperCase()} {pageData?.subject_label} exam pattern, KVS {examType.toUpperCase()} syllabus, EMRS teacher recruitment, NCERT notes, mock tests, previous year papers
          </p>
        </div>

        {/* CTA Section */}
        {!user && (
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl shadow-xl p-8 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Start?</h2>
            <p className="text-lg mb-6 opacity-90">
              Sign in to access notes, mock tests, and all features
            </p>
            <button
              onClick={() => {
                localStorage.setItem("returnTo", `/${examType}/${subject}`);
                navigate("/");
              }}
              data-testid="subject-signin-btn"
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Sign In / Register
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
